import * as THREE from "three";
import { Ceiling, CeilingDownlight, Baseboard, DayWindow, OfficeChair } from "./RoomKit";

/**
 * Procedural office for the PHYSICAL social-engineering scenario (impersonation /
 * tailgating -> hardware keylogger). Zero external assets, lit/shadowed to read
 * as a believable workplace. Key anchors line up with the scenario waypoints:
 *   - door + wall intercom on the back wall, left  (~[-2.4,1.05,-2.1] / [-1.5,1.45,-2.06])
 *   - desk + monitor centre                        (monitor [0,0.95,-0.35])
 *   - confidential documents on the desk           (~[-0.55,0.78,-0.02])
 *   - PC tower with USB ports, front-right of desk (~[0.95,0,0.0], ports ~y0.55)
 * Keep these if you edit this, or retune the scenario's camera waypoints.
 */
export function SocialOffice() {
  return (
    <group>
      {/* floor (carpet) */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[18, 18]} />
        <meshStandardMaterial color="#5d6470" roughness={1} />
      </mesh>
      {/* rug under the desk */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0.2]} receiveShadow>
        <planeGeometry args={[4.4, 3.2]} />
        <meshStandardMaterial color="#3a4250" roughness={1} />
      </mesh>

      {/* back wall */}
      <mesh position={[0, 2.1, -2.2]} receiveShadow>
        <boxGeometry args={[18, 4.2, 0.2]} />
        <meshStandardMaterial color="#d7dde3" roughness={0.95} />
      </mesh>
      {/* side wall (right) */}
      <mesh position={[5.2, 2.1, 0]} rotation={[0, -Math.PI / 2, 0]} receiveShadow>
        <boxGeometry args={[10, 4.2, 0.2]} />
        <meshStandardMaterial color="#cdd4db" roughness={0.95} />
      </mesh>
      {/* side wall (left) — without it the open left side rendered as a big black
          void (the dark backdrop) in the establish shot. */}
      <mesh position={[-5.2, 2.1, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <boxGeometry args={[10, 4.2, 0.2]} />
        <meshStandardMaterial color="#cdd4db" roughness={0.95} />
      </mesh>

      <EntryDoor />
      <Intercom3D />
      {/* entrance / break area dressing on the left so the corner reads as a real
          office entrance instead of an empty black wall. */}
      <NoticeBoard />
      <CoffeeStation />
      <WaterDispenser />

      {/* desk top */}
      <mesh position={[0, 0.74, -0.2]} castShadow receiveShadow>
        <boxGeometry args={[2.4, 0.05, 1.0]} />
        <meshStandardMaterial color="#2c333b" roughness={0.5} metalness={0.1} />
      </mesh>
      {[
        [-1.1, 0.37, 0.2],
        [1.1, 0.37, 0.2],
        [-1.1, 0.37, -0.6],
        [1.1, 0.37, -0.6],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.06, 0.74, 0.06]} />
          <meshStandardMaterial color="#1b2127" metalness={0.4} roughness={0.4} />
        </mesh>
      ))}

      {/* monitor (off/dark) */}
      <group position={[0, 0.95, -0.35]}>
        <mesh castShadow>
          <boxGeometry args={[0.62, 0.38, 0.03]} />
          <meshStandardMaterial color="#0a0d11" roughness={0.4} />
        </mesh>
        <mesh position={[0, 0, 0.018]}>
          <planeGeometry args={[0.58, 0.34]} />
          <meshStandardMaterial color="#0a0d11" roughness={0.5} metalness={0.2} />
        </mesh>
        <mesh position={[0, -0.27, 0]} castShadow>
          <boxGeometry args={[0.08, 0.16, 0.06]} />
          <meshStandardMaterial color="#11161b" />
        </mesh>
        <mesh position={[0, -0.35, 0.02]} castShadow>
          <boxGeometry args={[0.22, 0.02, 0.12]} />
          <meshStandardMaterial color="#11161b" />
        </mesh>
      </group>

      {/* keyboard + mouse */}
      <mesh position={[0, 0.77, 0.12]} castShadow>
        <boxGeometry args={[0.45, 0.02, 0.16]} />
        <meshStandardMaterial color="#20272e" roughness={0.6} />
      </mesh>
      <mesh position={[0.34, 0.77, 0.14]} castShadow>
        <boxGeometry args={[0.06, 0.02, 0.1]} />
        <meshStandardMaterial color="#20272e" roughness={0.6} />
      </mesh>

      <ConfidentialDocs />
      <DeskPhone />
      <PcTowerWithUsb />
      <PrinterCabinet />
      <FloorPlant />
      <OfficeChair position={[0, 0, 0.8]} />

      {/* finished interior: ceiling + recessed lights, skirting, daylight windows */}
      <Ceiling w={12} d={8} z={0.2} />
      <CeilingDownlight position={[-2.4, 4.05, -0.4]} intensity={1.0} />
      <CeilingDownlight position={[2.4, 4.05, -0.4]} intensity={1.0} />
      <CeilingDownlight position={[-2.4, 4.05, 1.2]} intensity={1.0} />
      <CeilingDownlight position={[2.4, 4.05, 1.2]} intensity={1.0} />
      {/* skirting along the three walls */}
      <Baseboard position={[0, 0.06, -2.08]} length={12} horizontal />
      <Baseboard position={[-5.08, 0.06, 0]} length={8} horizontal={false} />
      <Baseboard position={[5.08, 0.06, 0]} length={8} horizontal={false} />
      {/* daylight windows: one on the back wall (right of the door), one on the
          big right wall, so the room is bright + less blank */}
      <DayWindow position={[2.7, 1.65, -2.07]} w={2.2} h={1.7} spill={1.0} />
      <DayWindow position={[5.08, 1.65, 0.4]} rotationDeg={[0, -90, 0]} w={2.4} h={1.7} spill={0.9} />
    </group>
  );
}

/** A door set into the back wall (left), with frame, handle and a small wired-glass pane. */
function EntryDoor() {
  return (
    <group position={[-2.4, 0, -2.09]}>
      {/* frame */}
      <mesh position={[0, 1.05, 0]} castShadow>
        <boxGeometry args={[1.14, 2.18, 0.08]} />
        <meshStandardMaterial color="#aeb6bd" roughness={0.7} />
      </mesh>
      {/* leaf */}
      <mesh position={[0, 1.04, 0.03]} castShadow>
        <boxGeometry args={[0.98, 2.04, 0.06]} />
        <meshStandardMaterial color="#9aa3ab" roughness={0.6} metalness={0.1} />
      </mesh>
      {/* vision panel */}
      <mesh position={[0, 1.5, 0.07]}>
        <planeGeometry args={[0.42, 0.62]} />
        <meshStandardMaterial color="#5f7682" roughness={0.2} metalness={0.3} transparent opacity={0.55} />
      </mesh>
      {/* handle */}
      <mesh position={[0.36, 1.02, 0.08]} castShadow>
        <boxGeometry args={[0.05, 0.16, 0.04]} />
        <meshStandardMaterial color="#2b3138" metalness={0.6} roughness={0.3} />
      </mesh>
      {/* kickplate */}
      <mesh position={[0, 0.18, 0.07]}>
        <planeGeometry args={[0.9, 0.22]} />
        <meshStandardMaterial color="#7e878f" metalness={0.5} roughness={0.4} />
      </mesh>
    </group>
  );
}

/** Wall-mounted video door station beside the door (the speaker phone). The
 *  dark screen lights up via the DOM intercom overlay once the buzzer rings. */
function Intercom3D() {
  return (
    <group position={[-1.5, 1.45, -2.06]}>
      {/* housing */}
      <mesh castShadow>
        <boxGeometry args={[0.3, 0.46, 0.05]} />
        <meshStandardMaterial color="#1b2026" roughness={0.5} metalness={0.2} />
      </mesh>
      {/* screen (off) */}
      <mesh position={[0, 0.07, 0.027]}>
        <planeGeometry args={[0.24, 0.26]} />
        <meshStandardMaterial color="#070a0d" roughness={0.5} metalness={0.2} />
      </mesh>
      {/* speaker grille dots */}
      {Array.from({ length: 9 }).map((_, i) => (
        <mesh key={i} position={[-0.07 + (i % 3) * 0.07, -0.13 - Math.floor(i / 3) * 0.03, 0.027]}>
          <circleGeometry args={[0.008, 10]} />
          <meshStandardMaterial color="#0b0e12" />
        </mesh>
      ))}
      {/* call button */}
      <mesh position={[0.0, -0.06, 0.028]}>
        <circleGeometry args={[0.018, 16]} />
        <meshStandardMaterial
          color="#2fd4a7"
          emissive={new THREE.Color("#1f8f6f")}
          emissiveIntensity={0.7}
        />
      </mesh>
    </group>
  );
}

/** A clearly-visible stack of confidential papers + an open manila folder on the
 *  desk, front-left in the desk shot (clean-desk teaching hook). */
function ConfidentialDocs() {
  return (
    <group position={[-0.52, 0, 0.04]} rotation={[0, 0.18, 0]}>
      {/* open manila folder underneath, fanned toward the camera */}
      <mesh position={[0.02, 0.767, 0.02]} castShadow>
        <boxGeometry args={[0.46, 0.014, 0.34]} />
        <meshStandardMaterial color="#d9b066" roughness={0.9} />
      </mesh>
      {/* paper stack */}
      <mesh position={[0, 0.78, 0]} castShadow>
        <boxGeometry args={[0.34, 0.022, 0.44]} />
        <meshStandardMaterial color="#f3f4f6" roughness={0.95} />
      </mesh>
      {/* top sheet */}
      <mesh position={[0.005, 0.792, 0.006]} rotation={[0, -0.06, 0]} castShadow>
        <boxGeometry args={[0.34, 0.012, 0.44]} />
        <meshStandardMaterial color="#fcfdfe" roughness={0.95} />
      </mesh>
      {/* big red CONFIDENTIAL band across the top sheet (faces the camera) */}
      <mesh position={[0.005, 0.799, 0.14]} rotation={[-Math.PI / 2, 0, -0.06]}>
        <planeGeometry args={[0.34, 0.075]} />
        <meshStandardMaterial color="#c2384a" roughness={0.85} />
      </mesh>
      {/* a second header rule + a red stamp box to read as a sensitive document */}
      <mesh position={[0.005, 0.799, -0.02]} rotation={[-Math.PI / 2, 0, -0.06]}>
        <planeGeometry args={[0.26, 0.012]} />
        <meshStandardMaterial color="#9aa7b2" roughness={0.9} />
      </mesh>
      <mesh position={[0.1, 0.799, -0.12]} rotation={[-Math.PI / 2, 0, -0.06]}>
        <planeGeometry args={[0.1, 0.06]} />
        <meshStandardMaterial color="#c2384a" roughness={0.85} transparent opacity={0.85} />
      </mesh>
    </group>
  );
}

/** Desk phone, right of the keyboard. */
function DeskPhone() {
  return (
    <group position={[0.66, 0.79, 0.0]} rotation={[0, -0.6, 0]}>
      <mesh castShadow>
        <boxGeometry args={[0.2, 0.05, 0.26]} />
        <meshStandardMaterial color="#15191f" roughness={0.5} />
      </mesh>
      <mesh position={[0, 0.05, -0.08]} castShadow>
        <boxGeometry args={[0.22, 0.04, 0.06]} />
        <meshStandardMaterial color="#0c0f13" />
      </mesh>
    </group>
  );
}

/** A desktop tower standing on the floor, front-right of the desk, with visible
 *  USB ports on the top-front and a small inline keylogger dongle on a cable —
 *  the close-up camera frames this for the USB-reveal beat. */
function PcTowerWithUsb() {
  return (
    <group position={[0.98, 0, 0.02]}>
      {/* tower body */}
      <mesh position={[0, 0.46, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.24, 0.5, 0.5]} />
        <meshStandardMaterial color="#23282e" roughness={0.55} metalness={0.25} />
      </mesh>
      {/* front bezel */}
      <mesh position={[0, 0.46, 0.251]}>
        <planeGeometry args={[0.2, 0.46]} />
        <meshStandardMaterial color="#1a1e23" roughness={0.6} />
      </mesh>
      {/* power LED */}
      <mesh position={[0.06, 0.64, 0.252]}>
        <circleGeometry args={[0.008, 12]} />
        <meshStandardMaterial color="#3ad0ff" emissive={new THREE.Color("#1f8fb8")} emissiveIntensity={0.9} />
      </mesh>
      {/* top USB port block */}
      <mesh position={[0, 0.712, 0.12]}>
        <boxGeometry args={[0.16, 0.012, 0.1]} />
        <meshStandardMaterial color="#0e1216" roughness={0.5} />
      </mesh>
      {[-0.04, 0.0, 0.04].map((x, i) => (
        <mesh key={i} position={[x, 0.719, 0.12]}>
          <boxGeometry args={[0.022, 0.006, 0.05]} />
          <meshStandardMaterial color="#05080a" />
        </mesh>
      ))}
      {/* inline keylogger dongle inserted in the first top port */}
      <group position={[-0.04, 0.74, 0.12]}>
        <mesh castShadow>
          <boxGeometry args={[0.026, 0.05, 0.058]} />
          <meshStandardMaterial color="#0c0f13" roughness={0.5} />
        </mesh>
        {/* the keyboard plug stacked on top of the dongle */}
        <mesh position={[0, 0.05, 0]} castShadow>
          <boxGeometry args={[0.022, 0.03, 0.05]} />
          <meshStandardMaterial color="#16191e" roughness={0.6} />
        </mesh>
      </group>
    </group>
  );
}

/** A side cabinet with a printer on top (right-back of the desk). */
function PrinterCabinet() {
  return (
    <group position={[2.15, 0, -0.55]}>
      <mesh position={[0, 0.32, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.7, 0.64, 0.6]} />
        <meshStandardMaterial color="#2a3138" roughness={0.7} />
      </mesh>
      <mesh position={[0, 0.74, 0]} castShadow>
        <boxGeometry args={[0.56, 0.22, 0.46]} />
        <meshStandardMaterial color="#e6e8ea" roughness={0.6} />
      </mesh>
      <mesh position={[0, 0.86, -0.05]} rotation={[-0.5, 0, 0]} castShadow>
        <boxGeometry args={[0.5, 0.02, 0.16]} />
        <meshStandardMaterial color="#c6cace" roughness={0.6} />
      </mesh>
      <mesh position={[0, 0.7, 0.231]}>
        <planeGeometry args={[0.46, 0.04]} />
        <meshStandardMaterial color="#10141a" />
      </mesh>
      <mesh position={[0.2, 0.8, 0.231]}>
        <circleGeometry args={[0.012, 12]} />
        <meshStandardMaterial color="#3ad07a" emissive={new THREE.Color("#1f8f50")} emissiveIntensity={0.8} />
      </mesh>
    </group>
  );
}

/** Floor-standing potted plant (left, by the door). */
function FloorPlant() {
  return (
    <group position={[-1.95, 0, 0.2]}>
      <mesh position={[0, 0.26, 0]} castShadow>
        <cylinderGeometry args={[0.18, 0.13, 0.52, 16]} />
        <meshStandardMaterial color="#9a6a44" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.5, 0]}>
        <cylinderGeometry args={[0.185, 0.18, 0.04, 16]} />
        <meshStandardMaterial color="#7d5536" roughness={0.9} />
      </mesh>
      <mesh position={[0, 0.78, 0]} castShadow>
        <icosahedronGeometry args={[0.26, 1]} />
        <meshStandardMaterial color="#3f7a4f" roughness={1} flatShading />
      </mesh>
      <mesh position={[0.16, 0.95, 0.05]} castShadow>
        <icosahedronGeometry args={[0.18, 1]} />
        <meshStandardMaterial color="#46885a" roughness={1} flatShading />
      </mesh>
      <mesh position={[-0.14, 1.02, -0.04]} castShadow>
        <icosahedronGeometry args={[0.16, 1]} />
        <meshStandardMaterial color="#356b44" roughness={1} flatShading />
      </mesh>
    </group>
  );
}

/** A cork notice board on the back wall, left of the door, with pinned notes. */
function NoticeBoard() {
  const notes: Array<[number, number, string, number]> = [
    [-0.36, 0.16, "#fdf3a6", 0.05],
    [-0.05, 0.2, "#ffffff", -0.04],
    [0.28, 0.13, "#bfe0f2", 0.03],
    [-0.28, -0.16, "#ffd7d0", -0.05],
    [0.1, -0.18, "#d8f0cf", 0.04],
    [0.36, -0.12, "#ffffff", -0.03],
  ];
  return (
    <group position={[-3.75, 1.55, -2.08]}>
      {/* frame */}
      <mesh castShadow>
        <boxGeometry args={[1.34, 0.94, 0.05]} />
        <meshStandardMaterial color="#5a3d28" roughness={0.7} />
      </mesh>
      {/* cork surface */}
      <mesh position={[0, 0, 0.03]}>
        <planeGeometry args={[1.18, 0.78]} />
        <meshStandardMaterial color="#c69a5e" roughness={0.97} />
      </mesh>
      {/* pinned notes */}
      {notes.map(([x, y, col, rot], i) => (
        <group key={i} position={[x, y, 0.034]} rotation={[0, 0, rot]}>
          <mesh>
            <planeGeometry args={[0.2, 0.2]} />
            <meshStandardMaterial color={col} roughness={0.9} />
          </mesh>
          {/* pin */}
          <mesh position={[0, 0.07, 0.004]}>
            <circleGeometry args={[0.012, 12]} />
            <meshStandardMaterial color={i % 2 ? "#c2384a" : "#2f6f7e"} roughness={0.4} metalness={0.2} />
          </mesh>
        </group>
      ))}
    </group>
  );
}

/** A small counter against the left wall with a coffee machine + cups — break
 *  area dressing by the entrance. */
function CoffeeStation() {
  return (
    <group position={[-4.7, 0, -0.2]}>
      {/* counter cabinet (runs along the wall, z) */}
      <mesh position={[0, 0.42, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.5, 0.84, 1.3]} />
        <meshStandardMaterial color="#3a4048" roughness={0.7} />
      </mesh>
      {/* cabinet doors seam */}
      <mesh position={[0.26, 0.42, 0]}>
        <planeGeometry args={[0.02, 1.2]} />
        <meshStandardMaterial color="#11161b" />
      </mesh>
      {/* countertop */}
      <mesh position={[0, 0.86, 0]} castShadow>
        <boxGeometry args={[0.56, 0.05, 1.38]} />
        <meshStandardMaterial color="#d9d3c6" roughness={0.5} metalness={0.1} />
      </mesh>
      {/* coffee machine */}
      <group position={[0.02, 0.89, 0.15]}>
        <mesh position={[0, 0.18, 0]} castShadow>
          <boxGeometry args={[0.32, 0.36, 0.3]} />
          <meshStandardMaterial color="#15191f" roughness={0.5} metalness={0.3} />
        </mesh>
        {/* top hopper */}
        <mesh position={[0, 0.4, -0.02]} castShadow>
          <boxGeometry args={[0.26, 0.1, 0.22]} />
          <meshStandardMaterial color="#23282e" roughness={0.6} />
        </mesh>
        {/* dispenser head */}
        <mesh position={[0, 0.1, 0.13]}>
          <boxGeometry args={[0.12, 0.08, 0.05]} />
          <meshStandardMaterial color="#0c0f13" roughness={0.5} />
        </mesh>
        {/* status light */}
        <mesh position={[0.1, 0.28, 0.151]}>
          <circleGeometry args={[0.01, 12]} />
          <meshStandardMaterial color="#3ad07a" emissive={new THREE.Color("#1f8f50")} emissiveIntensity={0.8} />
        </mesh>
        {/* a cup under the spout */}
        <mesh position={[0, 0.0, 0.13]} castShadow>
          <cylinderGeometry args={[0.03, 0.026, 0.05, 14]} />
          <meshStandardMaterial color="#ffffff" roughness={0.6} />
        </mesh>
      </group>
      {/* stack of paper cups on the counter */}
      <mesh position={[-0.02, 0.93, -0.45]} castShadow>
        <cylinderGeometry args={[0.035, 0.03, 0.12, 14]} />
        <meshStandardMaterial color="#eef0f2" roughness={0.7} />
      </mesh>
    </group>
  );
}

/** A water cooler / dispenser in the back-left corner. */
function WaterDispenser() {
  return (
    <group position={[-4.55, 0, -1.7]}>
      {/* body */}
      <mesh position={[0, 0.55, 0]} castShadow receiveShadow>
        <boxGeometry args={[0.42, 1.1, 0.42]} />
        <meshStandardMaterial color="#eef1f3" roughness={0.6} metalness={0.1} />
      </mesh>
      {/* recessed tap panel */}
      <mesh position={[0, 0.74, 0.212]}>
        <planeGeometry args={[0.3, 0.26]} />
        <meshStandardMaterial color="#cdd3d8" roughness={0.6} />
      </mesh>
      {/* hot/cold taps */}
      {[[-0.06, "#c2384a"], [0.06, "#2f6f9e"]].map(([x, col], i) => (
        <mesh key={i} position={[x as number, 0.74, 0.24]} castShadow>
          <boxGeometry args={[0.04, 0.07, 0.05]} />
          <meshStandardMaterial color={col as string} roughness={0.5} />
        </mesh>
      ))}
      {/* drip tray */}
      <mesh position={[0, 0.6, 0.215]}>
        <boxGeometry args={[0.24, 0.02, 0.06]} />
        <meshStandardMaterial color="#9aa3ab" metalness={0.4} roughness={0.5} />
      </mesh>
      {/* collar */}
      <mesh position={[0, 1.13, 0]}>
        <cylinderGeometry args={[0.16, 0.2, 0.1, 18]} />
        <meshStandardMaterial color="#d7dde1" roughness={0.6} />
      </mesh>
      {/* inverted blue water bottle */}
      <mesh position={[0, 1.42, 0]} castShadow>
        <cylinderGeometry args={[0.17, 0.2, 0.5, 20]} />
        <meshStandardMaterial color="#9fd6ea" roughness={0.15} metalness={0.1} transparent opacity={0.7} />
      </mesh>
      {/* bottle neck (pointing down into the collar) */}
      <mesh position={[0, 1.16, 0]}>
        <cylinderGeometry args={[0.06, 0.1, 0.12, 16]} />
        <meshStandardMaterial color="#bfe6f2" roughness={0.2} transparent opacity={0.7} />
      </mesh>
    </group>
  );
}

